'use client';

import { useState, useEffect, useMemo, memo } from 'react';
import Image from 'next/image';
import { createPortal } from 'react-dom';
import DOMPurify from 'isomorphic-dompurify';
import parse from 'html-react-parser';
import { PopupComponentProps } from '@/components/popup/PopupHandler';

function PromotionPopup({ 
  closePopup, 
  popupData,
  translations
}: PopupComponentProps) {
  const [mounted, setMounted] = useState(false);

  const title = popupData?.title;
  const description = popupData?.description;
  const imageUrl = popupData?.imageUrl;
  const imageAlt = popupData?.imageAlt;
  const ctaText = popupData?.ctaText;
  const ctaRoute = popupData?.ctaRoute;
  const disclaimer = popupData?.disclaimer;
  const backgroundColor = popupData?.backgroundColor;

  const sanitizedDescription = useMemo(() => {
    if (!description) return null;
    const cleanHtml = DOMPurify.sanitize(description, {
      ALLOWED_TAGS: ['p', 'br', 'strong', 'em', 'u', 'i', 'b', 'ul', 'ol', 'li', 'a', 'span'],
      ALLOWED_ATTR: ['href', 'target', 'rel', 'class'],
      KEEP_CONTENT: true,
      ALLOW_DATA_ATTR: false,
      ALLOW_UNKNOWN_PROTOCOLS: false,
    });
    return parse(cleanHtml);
  }, [description]);

  const sanitizedDisclaimer = useMemo(() => {
    if (!disclaimer) return null;
    const cleanHtml = DOMPurify.sanitize(disclaimer, {
      ALLOWED_TAGS: ['p', 'br', 'strong', 'em', 'u', 'i', 'b', 'ul', 'ol', 'li', 'a', 'span'],
      ALLOWED_ATTR: ['href', 'target', 'rel', 'class'],
      KEEP_CONTENT: true,
      ALLOW_DATA_ATTR: false,
      ALLOW_UNKNOWN_PROTOCOLS: false,
    });
    return parse(cleanHtml);
  }, [disclaimer]);

  useEffect(() => {
    setMounted(true);
  }, []);

  if (!title || !ctaText || !ctaRoute) {
    return null;
  }

  const handleBackdropClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (e.target === e.currentTarget) {
      closePopup();
    }
  };

  const handleCtaClick = () => {
    window.open(ctaRoute, '_blank', 'noopener,noreferrer');
  };

  const content = (
    <div className="flex flex-col md:flex-row">
      {imageUrl && (
        <div 
          className="w-full md:w-1/2 relative aspect-square md:aspect-square overflow-hidden md:rounded-l-lg"
          style={{ backgroundColor: backgroundColor || '#f3f4f6' }}
        >
          <Image
            src={imageUrl}
            alt={imageAlt || ''}
            fill
            className="object-contain"
            sizes="(max-width: 768px) 100vw, 50vw"
          />
        </div>
      )}

      <div className={`w-full ${imageUrl ? 'md:w-1/2' : 'md:w-full'} px-6 py-6 md:py-8 flex flex-col justify-center`}>
        <div className="space-y-6">
          <div className="space-y-2">
            {title && (
              <h2 className="text-2xl font-bold text-gray-900">{title}</h2>
            )}
            {sanitizedDescription && (
              <div className="text-gray-600 prose prose-sm max-w-none">
                {sanitizedDescription}
              </div>
            )}
          </div>

          <button
            onClick={handleCtaClick}
            className="w-full px-6 py-3 bg-blue-600 text-white font-semibold rounded-lg hover:bg-blue-700 transition-all duration-200 shadow-md hover:shadow-lg transform hover:-translate-y-0.5"
          >
            {ctaText}
          </button>

          {sanitizedDisclaimer && (
            <div className="text-xs text-gray-500">
              {sanitizedDisclaimer}
            </div>
          )}
        </div>
      </div>
    </div>
  );

  if (!mounted) return null;

  return (
    <>
      {createPortal(
        <div
          className="fixed inset-0 bg-black/50 z-[999999]"
          onClick={handleBackdropClick}
        >
          <div
            className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-[90vw] max-w-[1000px] bg-white rounded-lg shadow-lg z-[1000000] max-h-[90vh] overflow-y-auto p-0"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="relative">
              {content}
              <button
                onClick={closePopup}
                className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 text-2xl leading-none cursor-pointer transition-colors bg-white rounded-full w-8 h-8 flex items-center justify-center shadow-md z-10"
                aria-label={translations.form.closePopup}
              >
                ×
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}
    </>
  );
}

export default memo(PromotionPopup);
